/**
 * Deadlines API (Termine)
 * Termine der Startseite, Termine eines Projekts und Pflege der manuellen und persönlichen Termine
 */
import { api } from "./client";
import {
  DeadlineItem,
  ManualDeadlineInput,
  ProjectSignals,
  PersonalDeadlineInput,
} from "../../types";

export type DeadlineScope = "mine" | "team";

interface GetDeadlinesOptions {
  scope?: DeadlineScope;
  projectId?: string;
}

/** Bezug, dessen Termine samt allem darunter geladen werden; leer heißt ganzes Projekt */
export interface DeadlineReferenceFilter {
  permitProcedureId?: string | null;
  evidenceId?: string | null;
}

export const deadlinesApi = {
  /** Die Problemsignale je Projekt – nur Projekte mit mindestens einem Signal */
  async getSignals(): Promise<ProjectSignals[]> {
    return api.get("/deadlines/signals");
  },

  async getDeadlines(options?: GetDeadlinesOptions): Promise<DeadlineItem[]> {
    const params = new URLSearchParams();
    if (options?.scope) params.set("scope", options.scope);
    if (options?.projectId) params.set("project_id", options.projectId);
    const queryString = params.toString();
    return api.get(`/deadlines${queryString ? `?${queryString}` : ""}`);
  },

  /** Termine eines Projekts, Antrags oder Nachweises – abgeleitete und manuelle, auch erledigte */
  async getProjectDeadlines(
    projectId: string,
    filter: DeadlineReferenceFilter = {},
  ): Promise<DeadlineItem[]> {
    const params = new URLSearchParams();
    if (filter.evidenceId) params.set("evidence_id", filter.evidenceId);
    else if (filter.permitProcedureId)
      params.set("permit_procedure_id", filter.permitProcedureId);
    const queryString = params.toString();
    return api.get(
      `/projects/${projectId}/deadlines${queryString ? `?${queryString}` : ""}`,
    );
  },

  async createManualDeadline(
    projectId: string,
    data: ManualDeadlineInput,
  ): Promise<DeadlineItem> {
    return api.post(`/projects/${projectId}/deadlines/manual`, data);
  },

  async updateManualDeadline(
    projectId: string,
    deadlineId: string,
    data: ManualDeadlineInput,
  ): Promise<DeadlineItem> {
    return api.put(
      `/projects/${projectId}/deadlines/manual/${deadlineId}`,
      data,
    );
  },

  async setManualDeadlineDone(
    projectId: string,
    deadlineId: string,
    done: boolean,
  ): Promise<DeadlineItem> {
    return api.put(
      `/projects/${projectId}/deadlines/manual/${deadlineId}/done`,
      {
        done,
      },
    );
  },

  async createPersonalDeadline(
    data: PersonalDeadlineInput,
  ): Promise<DeadlineItem> {
    return api.post("/deadlines/personal", data);
  },

  async updatePersonalDeadline(
    deadlineId: string,
    data: PersonalDeadlineInput,
  ): Promise<DeadlineItem> {
    return api.put(`/deadlines/personal/${deadlineId}`, data);
  },

  async setPersonalDeadlineDone(
    deadlineId: string,
    done: boolean,
  ): Promise<DeadlineItem> {
    return api.put(`/deadlines/personal/${deadlineId}/done`, { done });
  },

  async deletePersonalDeadline(deadlineId: string): Promise<void> {
    return api.delete(`/deadlines/personal/${deadlineId}`);
  },

  async deleteManualDeadline(
    projectId: string,
    deadlineId: string,
  ): Promise<void> {
    return api.delete(`/projects/${projectId}/deadlines/manual/${deadlineId}`);
  },
};
